<template>
    <div class="ds-urv-qr-screen" :class="{ 'ds-urv-qr-screen-offline': isOfflineMode }">
        <div class="ds-urv-shop">
            <h1 class="ds-urv-shop-name">{{ department.name }}</h1>
            <p class="ds-urv-date">{{ date }}</p>
            <p class="ds-urv-time">{{ time }}</p>
        </div>

        <div class="ds-urv-qr-content">
            <div class="ds-urv-qr-card" :class="{ 'ds-urv-qr-card-camera': isOfflineMode }">
                <section v-if="isOfflineMode" class="ds-urv-camera" :class="{ 'ds-urv-camera-success': isCheckinSuccess }" aria-label="Локальное считывание QR">
                    <div class="ds-urv-camera-preview">
                        <video ref="cameraVideoRef" class="ds-urv-camera-video" autoplay muted playsinline></video>
                    </div>
                    <div class="ds-urv-camera-message">
                        <p v-if="isCameraStarting" class="ds-urv-camera-status" role="status">Запускаем камеру…</p>
                        <p v-else-if="cameraError" class="ds-urv-camera-status ds-urv-camera-error" role="alert">{{ cameraError }}</p>
                        <p v-else-if="isCheckinPending" class="ds-urv-camera-status" role="status">Отправляем отметку…</p>
                        <p v-else-if="isCheckinSuccess" class="ds-urv-camera-status" role="status">{{ checkinMessage }}</p>
                        <p v-else-if="checkinMessage" class="ds-urv-camera-status ds-urv-camera-error" role="alert">{{ checkinMessage }}</p>
                        <p v-else class="ds-urv-camera-status" role="status">Покажите ответный QR-код</p>
                    </div>
                </section>
                <h2 class="ds-urv-qr-title">Отсканируйте QR-код</h2>
                <p class="ds-urv-qr-description">Отсканируйте код, чтобы отметиться на приход или уход со смены.</p>
                <div v-if="qrSvg" class="ds-urv-qr-code" aria-label="QR-код подразделения" v-html="qrSvg"></div>
                <div v-else class="ds-urv-loading">
                    {{ isQrLoading ? 'Загружаем QR-код…' : 'QR-код пока недоступен' }}
                </div>
                <p v-if="qrError" class="ds-urv-load-error" role="alert">
                    {{ qrError }}
                    <button type="button" @click="loadQr">Повторить</button>
                    <button type="button" @click="emit('request-key')">Ввести другой ключ</button>
                </p>
                <p class="ds-urv-qr-help">Если возникли трудности со сканированием, обратитесь в техническую поддержку.</p>
            </div>

            <aside class="ds-urv-employees" aria-label="Сотрудники">
                <div v-if="isEmployeesLoading && !workingEmployees.length && !waitingEmployees.length" class="ds-urv-loading">
                    Загружаем сотрудников…
                </div>
                <template v-else>
                    <UrvEmployeeList title="На смене" :employees="workingEmployees" />
                    <UrvEmployeeList title="Ждём на смене" :employees="waitingEmployees" />
                </template>
                <p v-if="employeesError" class="ds-urv-load-error" role="alert">
                    {{ employeesError }}
                    <button type="button" @click="loadEmployees">Повторить</button>
                    <button type="button" @click="emit('request-key')">Ввести другой ключ</button>
                </p>
            </aside>
        </div>
    </div>
    <div class="ds-urv-controls">
        <div class="ds-urv-mode-switch" role="group" aria-label="Режим считывания QR">
            <icon class="ds-urv-mode-icon" width="24" height="24"><IconWifi /></icon>
            <BooleanSwitch v-model="isOfflineMode">
                <span class="ds-urv-visually-hidden">Включить локальное считывание QR</span>
            </BooleanSwitch>
            <icon class="ds-urv-mode-icon" width="24" height="24"><IconWifiOff /></icon>
        </div>
        <btn class="ds-urv-return" @click="emit('close')">
            Вернуться к расписанию
        </btn>
    </div>
</template>

<script lang="ts">
// Общий для всех экземпляров экрана: закрытие и повторное открытие не снимают блокировку незавершённого POST
let isOfflineCheckinInFlight = false
</script>

<script setup lang="ts">
import { BrowserQRCodeReader, type IScannerControls } from '@zxing/browser'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
    getUrvEmployees,
    getUrvQrSvg,
    makeUrvOfflineCheckin,
    parseUrvOfflineCheckinQr,
    UrvApiError,
    urvInvalidQrMessage,
    type UrvDepartment,
    type UrvWorker,
} from '@/api/urv'
import { useAuthStore } from '@/stores/authStore'
import BooleanSwitch from '@/ui/BooleanSwitch.vue'
import IconWifi from '@/ui/icons/IconWifi.vue'
import IconWifiOff from '@/ui/icons/IconWifiOff.vue'
import UrvEmployeeList from './UrvEmployeeList.vue'

const props = defineProps<{
    department: UrvDepartment
}>()

const defaultQrUpdateInterval = 30_000
const urvModeStorageKey = 'main-front:urv-mode'
const authStore = useAuthStore()
const date = ref('')
const time = ref('')
const qrSvg = ref('')
const qrError = ref('')
const isQrLoading = ref(true)
const workingEmployees = ref<UrvWorker[]>([])
const waitingEmployees = ref<UrvWorker[]>([])
const employeesError = ref('')
const isEmployeesLoading = ref(true)
const isOfflineMode = ref(getStoredUrvMode() === 'offline')
const cameraVideoRef = ref<HTMLVideoElement | null>(null)
const isCameraStarting = ref(false)
const cameraError = ref('')
const checkinMessageDuration = 5_000
const isCheckinPending = ref(false)
const isCheckinSuccess = ref(false)
const checkinMessage = ref('')
let dateTimerId: ReturnType<typeof setInterval> | undefined
let qrTimerId: ReturnType<typeof setInterval> | undefined
let employeesTimerId: ReturnType<typeof setInterval> | undefined
let isQrRequestInFlight = false
let isEmployeesRequestInFlight = false
let isEmployeesReloadRequested = false
let isScreenUnmounted = false
let scannerControls: IScannerControls | undefined
let cameraSessionId = 0
let checkinMessageTimerId: ReturnType<typeof setTimeout> | undefined
const acceptedQrTexts = new Set<string>()

function getStoredUrvMode() {
    const storedMode = localStorage.getItem(urvModeStorageKey)
    return storedMode === 'offline' || storedMode === 'online' ? storedMode : 'online'
}

function getQrUpdateInterval() {
    const settings: unknown = authStore.settings

    if (!settings || typeof settings !== 'object') {
        return defaultQrUpdateInterval
    }

    const qrUpdateTime = (settings as Record<string, unknown>).QR_UPDATE_TIME
    const qrUpdateTimeInSeconds = typeof qrUpdateTime === 'number'
        ? qrUpdateTime
        : typeof qrUpdateTime === 'string' ? Number(qrUpdateTime) : Number.NaN

    return Number.isFinite(qrUpdateTimeInSeconds) && qrUpdateTimeInSeconds > 0
        ? qrUpdateTimeInSeconds * 1_000
        : defaultQrUpdateInterval
}

const updateDateTime = () => {
    const now = new Date()
    date.value = new Intl.DateTimeFormat('ru-RU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    }).format(now)
    time.value = new Intl.DateTimeFormat('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(now)
}

const emit = defineEmits<{
    (event: 'close'): void
    (event: 'session-invalid'): void
    (event: 'request-key'): void
}>()

async function loadQr() {
    if (isQrRequestInFlight) return

    isQrRequestInFlight = true
    isQrLoading.value = true
    qrError.value = ''

    try {
        qrSvg.value = await getUrvQrSvg(props.department.id)
    } catch (error) {
        handleRequestError(error, (message) => {
            qrError.value = message
        })
    } finally {
        isQrLoading.value = false
        isQrRequestInFlight = false
    }
}

async function loadEmployees() {
    if (isEmployeesRequestInFlight) return

    isEmployeesRequestInFlight = true
    isEmployeesLoading.value = true
    employeesError.value = ''

    try {
        const employees = await getUrvEmployees(props.department.id)
        workingEmployees.value = employees.on_shift
        waitingEmployees.value = employees.waiting_for
    } catch (error) {
        handleRequestError(error, (message) => {
            employeesError.value = message
        })
    } finally {
        isEmployeesLoading.value = false
        isEmployeesRequestInFlight = false

        if (isEmployeesReloadRequested && !isScreenUnmounted) {
            isEmployeesReloadRequested = false
            void loadEmployees()
        }
    }
}

function refreshEmployeesNow() {
    if (isEmployeesRequestInFlight) {
        isEmployeesReloadRequested = true
        return
    }

    void loadEmployees()
}

function handleRequestError(error: unknown, setError: (message: string) => void) {
    if (error instanceof UrvApiError && error.isSessionInvalid) {
        emit('session-invalid')
        return
    }

    setError(error instanceof Error ? error.message : 'Не удалось получить данные')
}

function clearCheckinMessage() {
    checkinMessage.value = ''
    isCheckinSuccess.value = false

    if (checkinMessageTimerId !== undefined) {
        clearTimeout(checkinMessageTimerId)
        checkinMessageTimerId = undefined
    }
}

function clearQrResult() {
    isCheckinPending.value = false
    clearCheckinMessage()
    acceptedQrTexts.clear()
}

function showCheckinMessage(message: string, isSuccess: boolean) {
    clearCheckinMessage()
    checkinMessage.value = message
    isCheckinSuccess.value = isSuccess
    checkinMessageTimerId = setTimeout(clearCheckinMessage, checkinMessageDuration)
}

async function submitOfflineCheckin(qrText: string, sessionId: number) {
    const payload = parseUrvOfflineCheckinQr(qrText)

    if (!payload) {
        showCheckinMessage(urvInvalidQrMessage, false)
        return
    }

    isOfflineCheckinInFlight = true
    clearCheckinMessage()
    isCheckinPending.value = true

    try {
        const message = await makeUrvOfflineCheckin(payload)
        if (sessionId !== cameraSessionId) return

        showCheckinMessage(message, true)
        refreshEmployeesNow()
    } catch (error) {
        if (sessionId !== cameraSessionId) return

        if (error instanceof UrvApiError && error.isSessionInvalid) {
            emit('session-invalid')
            return
        }

        showCheckinMessage(error instanceof Error ? error.message : 'Результат отметки не подтверждён', false)
    } finally {
        isOfflineCheckinInFlight = false
        if (sessionId === cameraSessionId) isCheckinPending.value = false
    }
}

function stopCamera() {
    cameraSessionId += 1
    scannerControls?.stop()
    scannerControls = undefined
    isCameraStarting.value = false
}

function getCameraErrorMessage(error: unknown) {
    const errorName = error instanceof DOMException ? error.name : ''

    if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
        return 'Не удалось получить доступ к камере. Разрешите доступ к камере и откройте страницу по защищённому соединению.'
    }

    if (errorName === 'NotFoundError' || errorName === 'OverconstrainedError') {
        return 'Камера не найдена. Подключите доступную камеру и повторите попытку.'
    }

    if (errorName === 'NotReadableError') {
        return 'Камера занята другим приложением. Закройте его и повторите попытку.'
    }

    return 'Не удалось запустить камеру. Попробуйте выключить и включить локальный режим снова.'
}

async function startCamera() {
    const videoElement = cameraVideoRef.value

    if (!isOfflineMode.value || !videoElement) return

    stopCamera()
    clearQrResult()
    cameraError.value = ''

    if (!window.isSecureContext) {
        cameraError.value = 'Для доступа к камере откройте страницу по защищённому соединению HTTPS.'
        return
    }

    if (!navigator.mediaDevices?.getUserMedia) {
        cameraError.value = 'Этот браузер не поддерживает доступ к камере. Откройте страницу в браузере с поддержкой камеры.'
        return
    }

    isCameraStarting.value = true
    const sessionId = cameraSessionId
    const codeReader = new BrowserQRCodeReader()

    try {
        const controls = await codeReader.decodeFromConstraints(
            {
                audio: false,
                video: { facingMode: { ideal: 'environment' } },
            },
            videoElement,
            (result) => {
                if (sessionId !== cameraSessionId || !isOfflineMode.value || !result) return
                if (isOfflineCheckinInFlight) return

                const qrText = result.getText()
                if (acceptedQrTexts.has(qrText)) return

                acceptedQrTexts.add(qrText)
                void submitOfflineCheckin(qrText, sessionId)
            },
        )

        if (sessionId !== cameraSessionId || !isOfflineMode.value) {
            controls.stop()
            return
        }

        scannerControls = controls
        isCameraStarting.value = false
    } catch (error) {
        if (sessionId !== cameraSessionId || !isOfflineMode.value) return

        isCameraStarting.value = false
        cameraError.value = getCameraErrorMessage(error)
    }
}

watch(isOfflineMode, (offlineMode) => {
    localStorage.setItem(urvModeStorageKey, offlineMode ? 'offline' : 'online')
    stopCamera()
    clearQrResult()
    cameraError.value = ''

    if (offlineMode) {
        void nextTick(startCamera)
    }
})

onMounted(() => {
    updateDateTime()
    loadQr()
    loadEmployees()
    dateTimerId = setInterval(updateDateTime, 1_000)
    qrTimerId = setInterval(loadQr, getQrUpdateInterval())
    employeesTimerId = setInterval(loadEmployees, 60_000)

    if (isOfflineMode.value) {
        void nextTick(startCamera)
    }
})

onBeforeUnmount(() => {
    isScreenUnmounted = true
    if (dateTimerId !== undefined) clearInterval(dateTimerId)
    if (qrTimerId !== undefined) clearInterval(qrTimerId)
    if (employeesTimerId !== undefined) clearInterval(employeesTimerId)
    stopCamera()
    clearQrResult()
})
</script>
